/**
 * Which spaces an event may plan in, given the track it runs on.
 *
 * A track is a partition of the spaces a single event books at a time, so that
 * concurrent events on disjoint tracks never contend for the same room or the
 * same stock. A space carries the track it belongs to — but that column is
 * optional, and a venue can have tracks before every space has been sorted into
 * one.
 *
 * An event occupies a **set** of tracks — it may run across several when each is
 * free for its window. An empty set is venue-wide.
 *
 * The rule for an unassigned space is that it stays venue-wide. Reading "no
 * track" as "no track's" would hide it from every event: a venue with one track
 * and three untracked spaces would seed events with zero plans and offer nothing
 * to add, which is how this rule was found. A space nobody has partitioned has
 * not been declared contended, and the one-event-per-track guarantee is enforced
 * on the event's track rather than on space membership.
 */
export function spaceOnTrack(spaceTrackId: string | null, eventTrackIds: readonly string[]): boolean {
  // No tracks means venue-wide, which is what a null `eventTrackId` used to
  // mean: every space is available.
  if (eventTrackIds.length === 0) return true
  return spaceTrackId === null || eventTrackIds.includes(spaceTrackId)
}

/**
 * The tracks an event occupies, from whatever carries them.
 *
 * A small adapter, because the event's tracks are read from two shapes: a DTO
 * (`trackIds`) and a Prisma row (`trackAssignments`). Keeping the coercion in
 * one place is what stops the "is this one track or a list" question being
 * answered differently in nine files.
 *
 * It used to read a third shape — the single-valued `Event.eventTrackId` — and
 * to treat an *empty* assignment list as "written by the previous release" and
 * fall through to it. Both the column and that fallback are gone: empty now
 * means what it says, venue-wide. See `docs/event-scheduling.md`.
 */
export function trackIdsOf(source: {
  trackIds?: readonly string[] | undefined
  trackAssignments?: readonly { eventTrackId: string }[] | undefined
}): string[] {
  if (source.trackIds !== undefined) return [...source.trackIds]
  if (source.trackAssignments !== undefined) return source.trackAssignments.map((a) => a.eventTrackId)
  return []
}

/**
 * The event's tracks as id **and name**, with {@link trackIdsOf}'s precedence.
 *
 * Here rather than at the call site because the booking conflict has to *name*
 * the track it is refusing ("Rodriguez Wedding already has the Barn track").
 */
export function trackNamesOf(source: {
  trackAssignments?: readonly { eventTrackId: string; eventTrack: { name: string } }[] | undefined
}): Map<string, string> {
  const names = new Map<string, string>()
  for (const a of source.trackAssignments ?? []) names.set(a.eventTrackId, a.eventTrack.name)
  return names
}

/**
 * The spaces an event may plan in.
 *
 * One implementation, because there were three and two of them disagreed. The
 * editor filtered with {@link spaceOnTrack} and so offered unassigned spaces;
 * the phase list and the import panel intersected against the track's own
 * `spaceIds` and so did not, which is the reading {@link spaceOnTrack}'s comment
 * calls out as the bug that produced events with no plans at all. A dead
 * `spaceInEventTrack` on the API made the same mistake in SQL.
 *
 * Membership is read off the space, not off the track's list, so an unassigned
 * space stays venue-wide.
 */
export function bookableSpaces<T extends { eventTrackId: string | null }>(
  spaces: readonly T[],
  eventTrackIds: readonly string[],
): T[] {
  return spaces.filter((space) => spaceOnTrack(space.eventTrackId, eventTrackIds))
}
